import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IPageResult } from '../interfaces/page-result';
import { ReadService } from './read.service';
import { ISupplierFeedback } from '../interfaces/supplier-feedback/supplier-feedback';
import { ISupplierFeedbackType } from '../interfaces/supplier-feedback/supplier-feedback-type';
import { ISupplierFeedbackFilter } from '../interfaces/supplier-feedback/supplier-feedback-filter';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class SupplierFeedbackService extends ReadService<
  ISupplierFeedback,
  ISupplierFeedbackFilter
> {
  url = '/api/supplierFeedback';
  entityTitle = 'Supplier Feedback';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  public getPageForJob(jobId: number, filter: ISupplierFeedbackFilter): Promise<any> {
    let pageUrl = this.url + '/job/' + jobId;

    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get<IPageResult<ISupplierFeedback>>(pageUrl, { params: params }),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getRecent(supplierId: number, howMany: number): Promise<ISupplierFeedback[]> {
    let pageUrl = `${this.url}/getRecentFeedback/${supplierId}/${howMany}`;
    return this.http.get<ISupplierFeedback[]>(pageUrl).toPromise();
  }

  public getTypes(): Promise<ISupplierFeedbackType[]> {
    return this.handleRequest(
      this.http.get(this.url + '/types'),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getOutcomes(): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/outcomes'),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public createFeedback(feedback): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/create', feedback),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public editFeedback(feedback): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/edit', feedback),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public addFeedbackDetail(body) {
    let successMsg = 'The message has been added to the supplier feedback history.';
    return this.handleRequest(
      this.http.post(this.url + '/addFeedbackMessage', body),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public resolveFeedback(outcome) {
    let successMsg = 'The supplier feedback has been resolved.';
    return this.handleRequest(
      this.http.post(this.url + '/resolveFeedback', outcome),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public downloadToCSV(filter: ISupplierFeedbackFilter) {
    let filename = 'SupplierFeedback_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';

    let params = this.constructHttpParams(filter);
    let report = this.http.get(`${this.url}/downloadToCSV`, {
      params: params,
      responseType: 'blob',
    });

    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Impossible to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  public handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }
}
